/**
 * useInitializeApp — bootstrap do app pós-reload.
 *
 * Executa UMA VEZ no mount do componente raiz (idealmente dentro do <App/>):
 *
 *   1. Lê `refreshToken` do auth.store (já hidratado do localStorage no boot).
 *   2. Se não há refreshToken           → estado `isReady = true` (não-autenticado:
 *                                          rotas protegidas redirecionarão para /login).
 *   3. Se há refreshToken               → POST /auth/refresh
 *      3a. Sucesso  → GET /me → set user + accessible_tenants → resolve tenant
 *                     ativo  → `isReady = true`.
 *      3b. Falha    → clearAuth + clearTenant + navega para /login → `isReady = true`.
 *
 * O hook retorna `{ isReady, isLoading, error }`:
 *  • `isReady`   — true quando o app pode renderizar (com ou sem sessão).
 *  • `isLoading` — true durante a sequência refresh→/me.
 *  • `error`     — Error capturado se o fluxo falhou (refresh ou /me).
 *
 * NOTA — admin global (modo basic):
 * No modo Basic não há refresh token. Se o usuário recarrega a página estando
 * em modo Basic, as credenciais (em memória) são perdidas e ele cai no caminho
 * "sem refreshToken" → vai para /login. Isto é intencional e está de acordo
 * com a spec §1 ("credenciais armazenadas como Base64 em auth.store" — sem
 * persistência).
 *
 * Referências de spec: §1 (Dois modos), §6 (Fluxo JWT), §8 (auth.store).
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { config } from '@/lib/config'
import { authService } from '@/services/auth.service'
import { executionsService } from '@/services/executions.service'
import { useAuthStore } from '@/store/auth.store'
import {
  useNotificationsStore,
  type Notification,
} from '@/store/notifications.store'
import { getPersistedTenantId, useTenantStore } from '@/store/tenant.store'
import type { Execution, MeResponse, Tenant, User } from '@/types/api'

// ---------------------------------------------------------------------------
// Helpers exportados (reutilizados por useAuth)
// ---------------------------------------------------------------------------

/**
 * Converte `accessible_tenants` (TenantAccessSummary[]) do payload de /me
 * no shape `Tenant[]` esperado pelo tenant.store.
 *
 * O endpoint `/me` retorna um sumário enxuto (sem `organization_id` nem
 * `is_active`). Para tenants que o usuário tem acesso de visualizar, é
 * seguro inferir `is_active = true` — o backend já filtra inativos.
 * `organization_id` é deixado como string vazia: queries que precisam
 * dessa info usam outros endpoints (ex.: detail do tenant).
 */
export function tenantsFromMeResponse(me: MeResponse): Tenant[] {
  return me.accessible_tenants.map((t) => ({
    id: t.tenant_id,
    name: t.tenant_name,
    organization_id: '',
    is_active: true,
    show_quality_evaluation: t.show_quality_evaluation === true,
  }))
}

/**
 * Converte `MeResponse` em `User` para popular o auth.store.
 *
 * `MeResponse` omite `is_active` (se está autenticado, está ativo).
 */
export function userFromMeResponse(me: MeResponse): User {
  return {
    id: me.id,
    email: me.email,
    name: me.name,
    primary_tenant_id: me.primary_tenant_id,
    primary_profile_id: me.primary_profile_id,
    is_active: true,
  }
}

/**
 * Decide qual tenant deve ser o ativo após login/bootstrap.
 *
 * Ordem de prioridade:
 *   1. `last_tenant_id` no localStorage (se ainda estiver em accessibleTenants).
 *   2. `primary_tenant_id` do /me (se acessível).
 *   3. Primeiro tenant de accessibleTenants.
 *   4. `null` se o usuário não tem nenhum tenant acessível.
 */
export function resolveInitialTenant(
  accessibleTenants: Tenant[],
  primaryTenantId: string | null | undefined,
): Tenant | null {
  if (accessibleTenants.length === 0) return null

  const lastId = getPersistedTenantId()
  if (lastId) {
    const last = accessibleTenants.find((t) => t.id === lastId)
    if (last) return last
  }

  if (primaryTenantId) {
    const primary = accessibleTenants.find((t) => t.id === primaryTenantId)
    if (primary) return primary
  }

  return accessibleTenants[0] ?? null
}

// ---------------------------------------------------------------------------
// Inicialização de notificações
// ---------------------------------------------------------------------------

/** Quantidade carregada como histórico inicial pós-login/reload. */
export const NOTIFICATIONS_HYDRATION_PAGE_SIZE = 20

/**
 * Mapeia uma `Execution` (vinda de `GET /executions`) para o formato
 * `Notification` armazenado no store. Marcada como `read: true` por design —
 * são notificações antigas, fornecidas apenas como contexto histórico.
 */
export function executionToNotification(execution: Execution): Notification {
  // Preferimos o instante de finalização; caímos em started_at e created_at
  // para execuções que ainda não terminaram (ex: queued/running aparecendo
  // na primeira página).
  const timestamp =
    execution.ended_at ?? execution.started_at ?? execution.created_at

  // O endpoint de listagem não traz `target_name`; usamos o `target_version_id`
  // como fallback humanamente identificável até o usuário abrir o detalhe.
  const title = execution.target_version_id || execution.id

  return {
    id: execution.id,
    type: 'execution_end',
    title,
    status: execution.status,
    execution_id: execution.id,
    timestamp,
    read: true,
  }
}

/**
 * Carrega as últimas 20 execuções e popula o `notifications.store` como
 * contexto histórico (todas marcadas como `read: true`).
 *
 * Best-effort: falhas (rede, 5xx) são silenciadas — o sininho simplesmente
 * inicia vazio e será populado pelo SSE global conforme novas execuções
 * acontecem. Não queremos impedir o boot do app por causa de uma listagem
 * acessória.
 */
export async function hydrateNotifications(): Promise<void> {
  try {
    // Só test cases: o feed também traz execuções de suite, que não são
    // `execution_end` nem abrem em /executions/:id.
    const response = await executionsService.list({
      page: 1,
      page_size: NOTIFICATIONS_HYDRATION_PAGE_SIZE,
      filters: { target_type: 'test_case' },
    })
    const notifications = response.data.map(executionToNotification)
    useNotificationsStore.getState().hydrate(notifications)
  } catch (err) {
    if (config.isDev) {
      // eslint-disable-next-line no-console
      console.warn('[useInitializeApp] failed to hydrate notifications:', err)
    }
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseInitializeAppResult {
  /** True quando o app pode começar a renderizar (com ou sem sessão). */
  isReady: boolean
  /** True durante a sequência refresh→/me. */
  isLoading: boolean
  /** Capturado quando o fluxo falha (refresh inválido, /me 5xx, etc.). */
  error: Error | null
}

// ---------------------------------------------------------------------------
// Module-level bootstrap singleton
// ---------------------------------------------------------------------------
// Garante que o bootstrap de sessão (refresh → /me → tenant) execute NO
// MÁXIMO uma vez por ciclo de vida da SPA, mesmo em React 18 Strict Mode
// (que monta/desmonta/remonta em dev). Sem isso, o Strict Mode dispara duas
// chamadas concorrentes de refresh, e o backend (com rotação de tokens)
// invalida o token na primeira chamada, fazendo a segunda falhar com 401.
//
// O resultado é compartilhado via `bootstrapPromise`. Cada instância do hook
// simplesmente aguarda a mesma promise e atualiza o state local ao resolver.
// ---------------------------------------------------------------------------

interface BootstrapResult {
  success: boolean
  error: Error | null
}

let bootstrapPromise: Promise<BootstrapResult> | null = null

function runBootstrap(): Promise<BootstrapResult> {
  if (bootstrapPromise) return bootstrapPromise

  bootstrapPromise = (async (): Promise<BootstrapResult> => {
    const { mode, refreshToken } = useAuthStore.getState()

    // Caso 0: sessão já ativa (ex.: logo após login via useAuth) → pronto.
    if (mode !== null) {
      return { success: true, error: null }
    }

    // Caso 1: sem refresh token → app pronto, não autenticado.
    if (!refreshToken) {
      return { success: true, error: null }
    }

    // Caso 2: refresh token presente — tenta reconstruir a sessão.
    try {
      const tokens = await authService.refresh(refreshToken)
      useAuthStore.getState().setJwtAuth(tokens)

      const me = await authService.getMe()
      useAuthStore.getState().setUser(userFromMeResponse(me))

      const tenants = tenantsFromMeResponse(me)
      useTenantStore.getState().setAccessibleTenants(tenants)

      const initialTenant = resolveInitialTenant(tenants, me.primary_tenant_id)
      if (initialTenant) {
        useTenantStore.getState().setActiveTenant(initialTenant)
        await hydrateNotifications()
      }

      return { success: true, error: null }
    } catch (e) {
      useAuthStore.getState().clearAuth()
      useTenantStore.getState().clearTenant()
      // Falha NÃO fica memorizada: senão, depois de um login bem-sucedido, o
      // ProtectedRoute remontado recebia de novo `success: false` e mandava
      // para /login — o login "não entrava" até um F5 (refresh token velho
      // no navegador, 2026-10-15). Quem já aguardava esta promise ainda vê a
      // falha; a próxima montagem refaz o bootstrap (caso 0 ou 1).
      bootstrapPromise = null
      const err = e instanceof Error ? e : new Error(String(e))
      return { success: false, error: err }
    }
  })()

  return bootstrapPromise
}

/** Reseta o singleton — usado em testes para isolar estado entre cenários. */
export function _resetBootstrap(): void {
  bootstrapPromise = null
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useInitializeApp(): UseInitializeAppResult {
  const [isReady, setReady] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false

    setLoading(true)

    runBootstrap().then((result) => {
      if (cancelled) return
      setError(result.error)
      setLoading(false)
      setReady(true)

      // Refresh falhou → redireciona para login.
      if (!result.success) {
        navigate('/login', { replace: true })
      }
    })

    return () => {
      cancelled = true
    }
  }, [navigate])

  return { isReady, isLoading, error }
}
